import type { ServiceKey, TokenStorage } from "./types";

const KEY_PREFIX = "aiva_";
const KEY_SUFFIX = "_refresh_token";

const ALL_SERVICES: ServiceKey[] = [
  "engage",
  "myqueue",
  "logocloud",
  "transcribe",
];

function refreshKey(service: ServiceKey): string {
  return `${KEY_PREFIX}${service}${KEY_SUFFIX}`;
}

/** Whether a storage key is one of our connector refresh-token keys. */
function isRefreshTokenKey(key: string): boolean {
  return key.startsWith(KEY_PREFIX) && key.endsWith(KEY_SUFFIX);
}

/**
 * AIVA-6685 (V-08): connector refresh tokens are stored in `sessionStorage`, not
 * `localStorage`, so a long-lived bearer isn't left in persistent storage for a
 * same-origin XSS to exfiltrate and replay against the connector token endpoint.
 *
 * This is safe here (unlike the MSAL dialog cache, which must stay in
 * localStorage) because the refresh token is only ever read/written by the
 * taskpane: the connector OAuth dialog returns just an authorization code, and
 * the taskpane performs the code exchange and every refresh itself — see
 * `cognito-adapter-base.ts`. No separate dialog window needs to read it back.
 *
 * Trade-off: a connector must be re-connected after a pane *reopen* (a fresh
 * taskpane window starts with empty sessionStorage) — the same trade-off already
 * accepted for the auth tokens in AuthStateProvider. Key names are unchanged so
 * a within-session reload still restores the connection.
 */
export function createSessionTokenStorage(
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = sessionStorage,
): TokenStorage {
  return {
    getRefreshToken(key) {
      return storage.getItem(refreshKey(key));
    },
    setRefreshToken(key, token) {
      storage.setItem(refreshKey(key), token);
    },
    clear(key) {
      storage.removeItem(refreshKey(key));
    },
    clearAll() {
      for (const key of ALL_SERVICES) {
        storage.removeItem(refreshKey(key));
      }
    },
  };
}

/**
 * AIVA-6685 (V-08): one-time migration — remove any connector refresh tokens a
 * pre-upgrade session left in `localStorage`, so the fix clears existing exposure
 * rather than only redirecting future writes. Scans by key shape so it catches
 * every service (including any not in {@link ALL_SERVICES}). No-op once none
 * remain, and safe to call repeatedly.
 */
export function purgeLegacyLocalRefreshTokens(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && isRefreshTokenKey(key)) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}
